/**
 * dadata.js
 * Модуль подсказок и заполнения формы адреса доставки на основе DADATA.
 * Требования: jQuery, load.js, utils.js
 */

import { load } from '../../system/load.js';
import { config } from '../../system/config.js';
import { ensureListContainer, createListItem, debounce, CityStorage } from './utils.js';

/**
 * @typedef {Object} DadataCityItem
 * @property {string} name        - Отображаемое имя (полная строка)
 * @property {string} city        - Короткое имя города для записи в поле
 * @property {string=} city_fias_id
 * @property {string=} city_kladr_id
 */

/**
 * @typedef {Object} DadataHouseItem
 * @property {string} house
 * @property {string=} postindex
 */

/**
 * Глобальный объект для управления подсказками и заполнением формы адреса.
 * Все селекторы/поля связаны с версткой из шаблона.
 */
export let dadata = {
	// ------------- Привязки к DOM -------------
	/** @type {jQuery} */
	addrForm: $("#address-form"),
	/** @type {jQuery} */
	countryField: $("#country"),

	/** Город доставки */
	city: {
		id: "#delivery-address-city",
		value: undefined,
		field: $("#delivery-address-city"),               // hidden для отправки выбранного города
		info: $("#delivery-address-city-info"),           // span.info для отображения выбранного
		select: $("#delivery-address-city-select"),       // input для набора и подсказок
		guidField: $("#delivery-address-city-guid"),      // hidden для FIAS GUID
	},

	/** Улица доставки */
	street: {
		id: "#delivery-address-street",
		value: undefined,
		field: $("#delivery-address-street"),
		info: $("#delivery-address-street-info"),
		select: $("#delivery-address-street-select")
	},

	/** Дом доставки */
	house: {
		id: "#delivery-address-house",
		value: undefined,
		field: $("#delivery-address-house"),
		info: $("#delivery-address-house-info"),
		select: $("#delivery-address-house-select")
	},

	/** Квартира/офис */
	flat: {
		id: "#delivery-address-flat",
		value: undefined,
		field: $("#delivery-address-flat")
	},

	/** Почтовый индекс */
	postindex: {
		id: "#delivery-address-index",
		value: undefined,
		field: $("#delivery-address-index"),
	},

	/** @type {jQuery} */
	errorMessage: $("#address-form-message"),

	/** Разное */
	selectric: ".selectric-items",

	cityField: "#delivery-address-city-select",
	streetField: "#delivery-address-street-select",
	houseField: "#delivery-address-house-select",
	innField: "#inn-search",

	// ------------- Настройки и состояния -------------
	country: "Россия",
	type: "ADDRESS",
	timeout: null,
	req: false,

	/**
	 * Контроллеры для отмены текущих запросов (чтобы старые ответы не затирали новые).
	 */
	_abort: {
		cities: null,
		streets: null,
		houses: null,
		company: null,
		geo: null,
	},

	/**
	 * Инициализация: подписки на события, стартовое состояние.
	 * Вызывается после загрузки CSS ниже.
	 * @returns {void}
	 */
	init: function () {
		$(".selectric-items .fa-pencil").hide();

		// Подсказка по ИНН: дебаунс 1с
		$(document).on("keyup change", dadata.innField, function (e) {
			if (e && [38,40,13,27].includes(e.which)) return;
			clearTimeout(dadata.timeout);
			dadata.timeout = setTimeout(dadata.get_company, 1000);
		});

		// Базовый контейнер под выпадашку для ИНН
		$(dadata.innField).after("<div class='helpinput'><ul class='selectric-list'></ul></div>");
		$(dadata.innField).parent().find(".selectric-list").parent().hide();

		// Делегирование: «карандаш» — открыть ввод (редактирование выбора)
		$(document).on("click", ".selectric-items .fa-pencil", function () {
			const $wrap = $(this).closest(".selectric-items");
			$(this).hide();
			$wrap.find(".selectric-input").slideDown().trigger('focus');
		});

		// Фокус в поле выбора — скрыть чужие
		$(document).on("focus", ".selectric-items .selectric-input", function () {
			dadata.hide_all_defined_fields($(this));
		});

		// Делегирование выбора из списков подсказок
		$(document).on("click", ".city-option", dadata.city_select);
		$(document).on("click", ".street-option", dadata.street_select);
		$(document).on("click", ".house-option", dadata.house_select);
		$(document)
			.off('click.dadataCompany', '.inn-option')
			.on('click.dadataCompany', '.inn-option', dadata.company_select);

		// Ввод с дебаунсом и минимальной длиной для подсказок
		$(document).on('keyup change', "#delivery-address-city-select", debounce(function (e) {
			if (e && [38,40,13,27].includes(e.which)) return;
			const q = dadata.city.select.val()?.trim() || "";
			if (q.length >= 2) dadata.get_cities(q);
		}, 250));

		$(document).on('keyup change', "#delivery-address-street-select", debounce(function (e) {
			if (e && [38,40,13,27].includes(e.which)) return;
			if (!dadata.city.value) return;
			const q = dadata.street.select.val()?.trim() || "";
			if (q.length >= 2) dadata.get_streets(q);
		}, 250));

		$(document).on('keyup change', "#delivery-address-house-select", debounce(function (e) {
			if (e && [38,40,13,27].includes(e.which)) return;
			if (!dadata.street.field.val()) return;
			const q = dadata.house.select.val()?.trim() || "";
			if (q.length >= 1) dadata.get_house(q);
		}, 250));

		// Скрытие выпадашки ИНН при клике вне вариантов (только контейнер ИНН)
		document.addEventListener("click", (event) => {
			if (event.target.matches(".inn-option")) return;
			const $w = $(dadata.innField).parent();
			clearTimeout(dadata.timeout);
			setTimeout(function () { $w.find(".helpinput").hide(); }, 300);
		}, false);

		// Стартовое: блокируем Street/House пока нет города
		dadata.street.select.prop("disabled", !dadata.city.value);
		dadata.house.select.prop("disabled", true);

		dadata.bindKeyboardNavigation();
	},

	// -------------------------------------------------------------------------
	// DADATA / API
	// -------------------------------------------------------------------------

	/**
	 * Геолокация → ближайшие адресные сущности (по lat/lng).
	 * @param {number|false} [lat=false]
	 * @param {number|false} [lng=false]
	 * @param {Function} [callback]
	 * @returns {void}
	 */
	getDataGeolocation: function (lat = false, lng = false, callback) {
		const body = $("body");
		if (!lat) lat = parseFloat(body.data("lat"));
		if (!lng) lng = parseFloat(body.data("lng"));
		if (!(lat && lng)) return;

		// Отменяем предыдущий геозапрос, если есть
		if (dadata._abort.geo) dadata._abort.geo.abort();
		dadata._abort.geo = new AbortController();

		const params = new FormData();
		params.append('lat', lat);
		params.append('lon', lng);

		load.request("/udata/dadata_api/get_geolocate/.json", {
			params,
			signal: dadata._abort.geo.signal
		}, callback);
	},

	/**
	 * Выполнить запрос городов по подстроке.
	 * @param {string} query
	 * @returns {void}
	 */
	get_cities: function (query) {
		if (dadata._abort.cities) dadata._abort.cities.abort();
		dadata._abort.cities = new AbortController();

		const params = new FormData();
		params.append('country', dadata.country);
		params.append('city', query || "");

		load.request("/udata/dadata_api/get_cities.json", {
			params,
			signal: dadata._abort.cities.signal
		}, dadata.select_cities);
	},

	/**
	 * Колбэк списка городов — рендер выпадающего списка.
	 * @param {{items?:{item: Record<string, DadataCityItem>}}} e
	 * @returns {void}
	 */
	select_cities: function (e) {
		if (!e?.items?.item) return;

		const $list = ensureListContainer(dadata.city.select);
		$list.empty().parent().show();

		for (const i in e.items.item) {
			const it = e.items.item[i];
			const $li = createListItem(it.name, {
				city: it.city,
				name: it.name,
				city_fias_id: it.city_fias_id,
				city_kladr_id: it.city_kladr_id
			});
			$li.addClass("city-option");
			$list.append($li);
		}
	},

	/**
	 * Выполнить запрос улиц по подстроке для выбранного города.
	 * @param {string} query
	 * @returns {void}
	 */
	get_streets: function (query) {
		if (!dadata.city.value) return;

		if (dadata._abort.streets) dadata._abort.streets.abort();
		dadata._abort.streets = new AbortController();

		const params = new FormData();
		params.append('country', dadata.country);
		params.append('city', dadata.city.value || dadata.city.select.data("city") || "");
		params.append('street', query || "");

		load.request("/udata/dadata_api/get_street.json", {
			params,
			signal: dadata._abort.streets.signal
		}, dadata.select_streets);
	},

	/**
	 * Колбэк списка улиц.
	 * @param {{items?:{item: Record<string, string>}}} e
	 * @returns {void}
	 */
	select_streets: function (e) {
		if (!e?.items?.item) return;

		const $list = ensureListContainer(dadata.street.select);
		$list.empty().parent().show();

		for (const i in e.items.item) {
			const name = e.items.item[i];
			const $li = createListItem(name, { street: name });
			$li.addClass("street-option");
			$list.append($li);
		}
	},

	/**
	 * Выполнить запрос домов по подстроке для выбранной улицы.
	 * @param {string} query
	 * @returns {void}
	 */
	get_house: function (query) {
		const val = query || dadata.house.select.val();
		if (!val) return;

		if (dadata._abort.houses) dadata._abort.houses.abort();
		dadata._abort.houses = new AbortController();

		const params = new FormData();
		params.append('country', dadata.country);
		params.append('city', dadata.city.value || "");
		params.append('street', dadata.street.field.val() || "");
		params.append('house', val);

		load.request("/udata/dadata_api/get_house.json", {
			params,
			signal: dadata._abort.houses.signal
		}, dadata.select_houses);
	},

	/**
	 * Колбэк списка домов.
	 * @param {{items?:{item: Record<string, DadataHouseItem>}}} e
	 * @returns {void}
	 */
	select_houses: function (e) {
		if (!e?.items?.item) return;

		const $list = ensureListContainer(dadata.house.select);
		$list.empty().parent().show();

		for (const i in e.items.item) {
			const it = e.items.item[i];
			const $li = createListItem(it.house, { postindex: it.postindex || "" });
			$li.addClass("house-option");
			$list.append($li);
		}
	},

	/**
	 * Запрос подсказок компаний по ИНН.
	 * @returns {boolean}
	 */
	get_company: function () {
		const url = "/udata/dadata_api/get_company.json";
		const inn = $(dadata.innField).val();
		if (typeof inn === "undefined") return false;

		if (dadata._abort.company) dadata._abort.company.abort();
		dadata._abort.company = new AbortController();

		const params = new FormData();
		params.set("inn", inn);

		load.request(url, {
			params,
			signal: dadata._abort.company.signal
		}, dadata.set_company);

		return true;
	},

	/**
	 * Выбор компании из подсказки ИНН.
	 * Использует уже существующие таймер/AbortController.
	 * @param {JQuery.ClickEvent} e
	 * @returns {void}
	 */
	company_select: function (e) {
		e.preventDefault();
		e.stopImmediatePropagation();

		const $t = $(e.currentTarget);

		// 1) Подставляем данные в форму
		$(".field_name").val($t.data("name"));
		$(".field_inn").val($t.data("inn"));
		$(".field_kpp").val($t.data("kpp"));
		$(".field_ogrn").val($t.data("ogrn"));
		$(".field_okpo").val($t.data("okpo"));
		$(".field_legal_address").val($t.data("legal_address"));
		$(".field_district").val($t.data("district"));
		$(".field_management_person").val($t.data("management_person"));
		$(".field_management_post").val($t.data("management_post"));

		// 2) Гасим хвосты
		clearTimeout(dadata.timeout);
		if (dadata._abort.company) {
			try { dadata._abort.company.abort(); } catch(_) {}
			dadata._abort.company = null;
		}

		// 3) Закрываем и чистим UI подсказок
		const $wrap = $(dadata.innField).parent();
		$wrap.find(".selectric-list").empty();
		$wrap.find(".helpinput").hide();

		// 4) (Опционально) очищаем и снимаем фокус с поля ввода ИНН,
		// чтобы не спровоцировать повторный запуск подсказок
		//$(dadata.innField).val("").trigger("change").blur();
	},

	/**
	 * Подстановка найденной компании в поля формы (разовый выбор).
	 * Примечание: result.items.item у API может быть:
	 *  - массивом;
	 *  - объектом с числовыми строковыми ключами ("0","1",...);
	 *  - одиночным объектом.
	 * Мы приводим всё это к массиву.
	 *
	 * @param {Object} result
	 * @param {{items?:{item:any}}} result
	 * @returns {void}
	 */
	set_company: function (result) {
		// 1) Нормализуем структуру items -> массив
		const raw = result && result.items && result.items.item;
		const items = Array.isArray(raw)
			? raw
			: (raw && typeof raw === 'object' ? Object.values(raw) : (raw ? [raw] : []));

		if (!items.length) return;

		// 2) Рендер списка вариантов
		const $list = $(dadata.innField).parent().find(".selectric-list");
		$list.parent().show();
		$list.empty();

		items.forEach((item) => {
			const text = `${item.name} (${item.inn})`;
			const $li = createListItem(text, {
				name: item.name,
				inn: item.inn,
				kpp: item.kpp,
				ogrn: item.ogrn,
				okpo: item.okpo,
				legal_address: item.legal_address,
				district: item.district,
				management_person: item.management_person,
				management_post: item.management_post
			});
			$li.addClass("inn-option");
			$list.append($li);
		});

		// 3) Разовый обработчик выбора компании
		/*
		document.addEventListener("click", (event) => {
			if (event.target && event.target.matches(".inn-option")) {
				const $t = $(event.target);

				$(".field_name").val($t.data("name"));
				$(".field_inn").val($t.data("inn"));
				$(".field_kpp").val($t.data("kpp"));
				$(".field_ogrn").val($t.data("ogrn"));
				$(".field_okpo").val($t.data("okpo"));
				$(".field_legal_address").val($t.data("legal_address"));
				$(".field_management_person").val($t.data("management_person"));
				$(".field_management_post").val($t.data("management_post"));

				// если нужно — скрыть список после выбора:
				$list.parent().hide();
			}
		});

		 */
	},

	// -------------------------------------------------------------------------
	// Обработчики выбора из подсказок
	// -------------------------------------------------------------------------

	/**
	 * Выбор города из подсказки.
	 * Сохраняет город в хранилище и триггерит change у #delivery-address-city.
	 * @param {JQuery.ClickEvent} e
	 * @returns {void}
	 */
	city_select: function (e) {
		e.preventDefault();
		e.stopImmediatePropagation();

		const $li = $(e.currentTarget);
		const city = ($li.data("city") || "").toString();
		const cityName = ($li.data("name") || city || "").toString();
		const fias = ($li.data("city_fias_id") || "").toString();

		// Сохраняем выбор (только по клику на подсказке)
		dadata.setCity(city);

		// Обновляем UI/hidden
		dadata.city.info.text(cityName).addClass("active").show();
		dadata.city.field.val(city).trigger('change');
		dadata.city.guidField.val(fias);

		// Дальнейшие поля
		dadata.street.select.prop("disabled", false);
		dadata.house.select.prop("disabled", true);

		// Закрываем подсказки и чистим зависимые поля
		dadata.hiddenSelectric(dadata.city.select);
		dadata.clearFields("street,house,flat,index");
	},

	/**
	 * Выбор улицы из подсказки.
	 * @param {JQuery.ClickEvent} e
	 * @returns {void}
	 */
	street_select: function (e) {
		e.preventDefault();
		e.stopImmediatePropagation();

		const $li = $(e.currentTarget);
		const value = $li.text();

		dadata.street.field.val(value);
		dadata.street.info.text(value).addClass("active").show();

		dadata.house.select.prop("disabled", false);

		dadata.hiddenSelectric(dadata.street.select);
		dadata.clearFields("house,flat,index");
	},

	/**
	 * Выбор дома из подсказки.
	 * @param {JQuery.ClickEvent} e
	 * @returns {void}
	 */
	house_select: function (e) {
		e.preventDefault();
		e.stopImmediatePropagation();

		const $li = $(e.currentTarget);
		const value = $li.text();
		const postindex = $li.data("postindex");

		dadata.house.field.val(value);
		dadata.house.info.text(value).addClass("active").show();

		if (postindex) {
			dadata.postindex.field.val(postindex).removeClass("error");
		}

		dadata.hiddenSelectric(dadata.house.select);
		dadata.clearFields("flat");
	},

	// -------------------------------------------------------------------------
	// Вспомогательные UI/валидация
	// -------------------------------------------------------------------------

	/**
	 * Скрыть список подсказок и переключить блок в режим "выбрано".
	 * Принимает любой элемент внутри .selectric-items — сама найдёт input.
	 * @param {jQuery|HTMLElement} anyEl
	 * @returns {void}
	 */
	hiddenSelectric: function (anyEl) {
		const $any = $(anyEl);
		const $wrap = $any.closest(".selectric-items");
		const $input = $wrap.find(".selectric-input");
		const $info = $wrap.find("span.info");

		// очищаем ввод
		$input.val("");

		if ($info.length && $info.text().trim() !== "") {
			$input.slideUp();
			$wrap.addClass("selected").removeClass("error");
		}

		$wrap.find(".selectric-list").empty().parent().hide();
		$wrap.find(".fa-pencil").show();
	},

	/**
	 * Очистка зависимых полей.
	 * @param {string} fields - Список через запятую: street,house,flat,index
	 * @returns {void}
	 */
	clearFields: function (fields) {
		const arr = (fields || "").split(",").map(s => s.trim()).filter(Boolean);
		arr.forEach((name) => {
			switch (name) {
				case "street":
					dadata.clearStreetBlock();
					break;
				case "house":
					dadata.clearHouseBlock();
					break;
				case "flat":
					dadata.clearFlatBlock();
					break;
				case "index":
					dadata.clearPostIndexBlock();
					break;
			}
		});
	},

	/**
	 * Очистка блока "Улица".
	 * @returns {void}
	 */
	clearStreetBlock: function () {
		const block = dadata.street.field.closest(".selectric-items");
		block.removeClass("selected");
		dadata.street.field.val("");
		dadata.street.info.removeClass("active").text("");
		block.find(".fa-pencil").hide();
		block.find(".selectric-input").slideDown();
		dadata.house.select.prop("disabled", true);
	},

	/**
	 * Очистка блока "Дом".
	 * @returns {void}
	 */
	clearHouseBlock: function () {
		const block = dadata.house.field.closest(".selectric-items");
		block.removeClass("selected");
		dadata.house.field.val("");
		dadata.house.info.removeClass("active").text("");
		block.find(".fa-pencil").hide();
		block.find(".selectric-input").slideDown();
	},

	/**
	 * Очистка поля "Квартира/офис".
	 * @returns {void}
	 */
	clearFlatBlock: function () {
		dadata.flat.field.val("");
	},

	/**
	 * Очистка поля "Почтовый индекс".
	 * @returns {void}
	 */
	clearPostIndexBlock: function () {
		dadata.postindex.field.val("");
	},

	/**
	 * Установить текущий город (и сохранить без jQuery cookie).
	 * Запускается ТОЛЬКО при выборе города из подсказки.
	 * @param {string} city
	 * @returns {void}
	 */
	setCity: function (city) {
		const c = (city || "").toString().trim();
		if (c) {
			CityStorage.set(c);
			dadata.city.value = c;
			return;
		}
		const saved = CityStorage.get();
		if (saved) dadata.city.value = saved;
	},

	/**
	 * Вернуть выбранную страну (по data-name у опции).
	 * @returns {string|undefined}
	 */
	get_field_country: function () {
		return dadata.countryField.find("option:selected").data("name");
	},

	/**
	 * Скрыть все чужие поля ввода, раскрыть текущее.
	 * @param {jQuery} _input - активный input
	 * @returns {void}
	 */
	hide_all_defined_fields: function (_input) {
		const curid = _input.attr("id");
		$(".selectric-items").each(function () {
			const $wrap = $(this);
			const $inp = $wrap.find(".selectric-input");
			const $info = $wrap.find("span.info");

			if ($inp.attr("id") !== curid) {
				if ($info.text() !== "") {
					$inp.slideUp();
					$wrap.find(".fa-pencil").show();
				}
				if ($info.text() === "") {
					$inp.val("");
				}
			}
		});
	},

	/* =========================
     * КЛАВИАТУРНАЯ НАВИГАЦИЯ
     * ========================= */

	bindKeyboardNavigation: function () {
		const self = this;

		$(document)
			.off("keydown.dadataNav")
			.on(
				"keydown.dadataNav",
				`${this.cityField}, ${this.streetField}, ${this.houseField}, ${this.innField}`,
				function (e) {
					self.onSuggestKeydown(e);
				}
			);

		// синхронизация с мышью
		$(document).on("mouseenter", ".helpinput li", function () {
			$(this)
				.addClass("active")
				.siblings()
				.removeClass("active");
		});
	},

	onSuggestKeydown: function (e) {
		const KEY = {
			UP: 38,
			DOWN: 40,
			ENTER: 13,
			ESC: 27
		};

		if (![KEY.UP, KEY.DOWN, KEY.ENTER, KEY.ESC].includes(e.which)) { return; }

		const ctx = this._getSuggestList(e.target);
console.log(ctx);
		if (!ctx || !ctx.items.length) { return; }

		let index = ctx.items.filter(".active").index();
console.log("index", index);
		if (e.which === KEY.DOWN) {
			e.preventDefault();
			index = index < ctx.items.length - 1 ? index + 1 : 0;
			console.log("index2", index);
			this._setActiveIndex(ctx.items, index);
		}

		if (e.which === KEY.UP) {
			e.preventDefault();
			index = index > 0 ? index - 1 : ctx.items.length - 1;
			this._setActiveIndex(ctx.items, index);
		}

		if (e.which === KEY.ENTER) {
			e.preventDefault();
			if (index === -1) {
				index = 0;
			}
			ctx.items.eq(index).trigger("click");
		}

		if (e.which === KEY.ESC) {
			ctx.container.hide();
		}
	},

	_getSuggestList: function (input) {
		const $input = $(input);
		const $container = $input
			.closest(".selectric-items, .helpinput")
			.find(".helpinput");

		if (!$container.length) {
			return null;
		}

		const $items = $container.find("li");

		return {
			container: $container,
			items: $items
		};
	},

	_setActiveIndex: function ($items, index) {
		const $item = $items
			.removeClass("active")
			.eq(index)
			.addClass("active");

		if ($item.length) {
			$item[0].scrollIntoView({
				block: "nearest"
			});
		}
	},
};

// Загрузка стилей, затем инициализация
load.css(config.template + 'css/vendor/dadata/dadata.css')
	.then(() => dadata.init())
	.catch(error => console.log(`Ошибка загрузки стилей DADATA: ${error.message}`));
